// optional project password for handover packages: PBKDF2 (SHA-256, 250 000 rounds) + AES-GCM 256; the password never travels in a package
export const ENC = 'giss-enc';
const ROUNDS = 250000, te = new TextEncoder(), td = new TextDecoder();
const PWK = (id) => `giss_pm_pw_${id}`;
export const getPw = (id) => sessionStorage.getItem(PWK(id)) || localStorage.getItem(PWK(id)) || '';
export const setPw = (id, pw) => { if (pw) localStorage.setItem(PWK(id), pw); else localStorage.removeItem(PWK(id)); };

const b64 = (u8) => { let s = ''; for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode(...u8.subarray(i, i + 0x8000)); return btoa(s); };
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
async function key(pw, salt) {
  const base = await crypto.subtle.importKey('raw', te.encode(pw), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', salt, iterations: ROUNDS, hash: 'SHA-256' }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
// project id + title stay readable so the office can match the package before asking for the password
export async function encrypt(obj, pw, kind) {
  const salt = crypto.getRandomValues(new Uint8Array(16)), iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await key(pw, salt), te.encode(JSON.stringify(obj))));
  return { format: ENC, v: 1, kind, projectId: obj.projectId, projectTitle: obj.projectTitle || obj.project?.title || '', createdAt: Date.now(), salt: b64(salt), iv: b64(iv), data: b64(data) };
}
export async function decrypt(env, pw) {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(env.iv) }, await key(pw, unb64(env.salt)), unb64(env.data));
  return JSON.parse(td.decode(plain));
}
// stored password first, then ask; wrong password -> ask again (max 3 times)
export async function openEnvelope(env, T) {
  let pw = getPw(env.projectId);
  for (let i = 0; i < 4; i++) {
    if (pw) { try { const o = await decrypt(env, pw); setPw(env.projectId, pw); return o; } catch { /* wrong password */ } }
    if (i === 3) break;
    pw = await askPassword(i || getPw(env.projectId) ? T.pwWrong : T.pwAsk.replace('{p}', env.projectTitle || ''), T);
    if (!pw) return null;
  }
  return null;
}
export function askPassword(msg, T) {
  return new Promise((res) => {
    const wrap = document.createElement('div');
    wrap.className = 'ask-backdrop'; wrap.style.zIndex = '4000'; wrap.setAttribute('data-testid', 'pm-pw-dialog');
    wrap.innerHTML = '<form class="ask-box" role="dialog"><p></p><input type="password" class="input" data-testid="pm-pw-input" autocomplete="off" style="width:100%;margin:6px 0 10px"><div class="ask-row"><button type="button" class="btn btn-outline" data-testid="pm-pw-cancel"></button><button type="submit" class="btn btn-red" data-testid="pm-pw-ok">OK</button></div></form>';
    wrap.querySelector('p').textContent = msg;
    const f = wrap.querySelector('form'), inp = wrap.querySelector('input'), no = wrap.querySelector('button');
    no.textContent = T.pwCancel;
    const done = (v) => { wrap.remove(); res(v); };
    no.onclick = () => done(null); f.onsubmit = (e) => { e.preventDefault(); done(inp.value || null); };
    document.body.appendChild(wrap); inp.focus();
  });
}
// package or return package ready for download: encrypted when the project has a password (asked once if this device lacks it)
export async function sealFor(p, obj, kind, T) {
  let pw = getPw(p.id);
  if (!pw && p.pm?.encOn) { pw = await askPassword(T.pwAsk.replace('{p}', p.title || ''), T); if (!pw) return null; setPw(p.id, pw); }
  return pw ? encrypt(obj, pw, kind) : obj;
}
